import type { OnboardingStep } from "@memorang/constants/config/schema";
import { formatList } from "@memorang/helpers/i18n";
import type { ExamDetails, StateDetails } from "@memorang/types";
import type { Category } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";
import { Avatar } from "../../components/Avatar";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingBox } from "./components/OnboardingBox";
import { LEVEL_OPTIONS } from "./constants";
import {
	getAvailableDiagnostics,
	getDiagnosticIcon,
	resolveI18n,
} from "./helpers";

type Props = {
	title: string;
	description: string | undefined;
};

type ReviewRowData = {
	rowLabel: string;
	value: string;
};

const findStepLabel = (
	steps: OnboardingStep[],
	stepType: string,
	locale: string,
) => {
	const s = steps.find((st) => st.step === stepType);
	return (
		s?.labeli18n?.[locale] ??
		s?.labeli18n?.["en-US"] ??
		(resolveI18n(s?.titlei18n, locale) || stepType)
	);
};

const buildLearnerTypeRow = (
	steps: OnboardingStep[],
	selectedLearnerTypeId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	const step = steps.find((s) => s.step === "learnerType");
	if (!selectedLearnerTypeId) {
		return null;
	}
	if (!step?.data) {
		return null;
	}
	const item = step.data.find(
		(d) =>
			(d.type ?? resolveI18n(d.titlei18n, "en-US")) === selectedLearnerTypeId,
	);
	if (!item?.titlei18n) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "learnerType", locale),
		value: resolveI18n(item.titlei18n, locale),
	};
};

const buildCategoryRow = (
	steps: OnboardingStep[],
	categories: Category[] | undefined,
	selectedCategoryId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	if (!selectedCategoryId) {
		return null;
	}
	if (!categories) {
		return null;
	}
	const category = categories.find((c) => c.id === selectedCategoryId);
	if (!category) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "chooseCategory", locale),
		value: category.name,
	};
};

const buildLevelRow = (
	steps: OnboardingStep[],
	selectedLevelId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	if (!selectedLevelId) {
		return null;
	}
	const level = LEVEL_OPTIONS.find((l) => l.id === selectedLevelId);
	if (!level) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "chooseLevel", locale),
		value: level.title,
	};
};

const buildStateRow = (
	steps: OnboardingStep[],
	derivedStates: StateDetails[],
	selectedStateCode: string | undefined,
	locale: string,
): ReviewRowData | null => {
	if (!selectedStateCode) {
		return null;
	}
	const state = derivedStates.find((s) => s.code === selectedStateCode);
	if (!state) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "chooseState", locale),
		value: state.name,
	};
};

const buildExamRow = (
	steps: OnboardingStep[],
	exams: ExamDetails[] | undefined,
	filteredExams: ExamDetails[],
	selectedExamId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	if (!selectedExamId) {
		return null;
	}
	const source = filteredExams.length > 0 ? filteredExams : (exams ?? []);
	const exam = source.find((e) => e.exam.id === selectedExamId);
	if (!exam) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "chooseExam", locale),
		value: exam.exam.name,
	};
};

const buildExamDateRow = (
	steps: OnboardingStep[],
	initialExamDate: Date | undefined,
	locale: string,
): ReviewRowData | null => {
	if (!initialExamDate) {
		return null;
	}
	const step = steps.find(
		(s) => s.step === "examDate" || s.step === "set-date",
	);
	const rowLabel =
		step?.labeli18n?.[locale] ??
		step?.labeli18n?.["en-US"] ??
		findStepLabel(steps, "examDate", locale);
	const value = initialExamDate.toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
	return { rowLabel, value };
};

const buildRankRow = (
	steps: OnboardingStep[],
	selectedRankId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	const step = steps.find((s) => s.step === "rank");
	if (!selectedRankId) {
		return null;
	}
	if (!step?.data) {
		return null;
	}
	const item = step.data.find(
		(d) =>
			(d.type ?? d.iconString ?? resolveI18n(d.titlei18n, "en-US")) ===
			selectedRankId,
	);
	if (!item?.titlei18n) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "rank", locale),
		value: resolveI18n(item.titlei18n, locale),
	};
};

const buildPlanRow = (
	steps: OnboardingStep[],
	selectedPlanId: string | undefined,
	locale: string,
): ReviewRowData | null => {
	const step = steps.find((s) => s.step === "plan");
	if (!selectedPlanId) {
		return null;
	}
	if (!step?.data) {
		return null;
	}
	const item = step.data.find(
		(d) => (d.type ?? resolveI18n(d.titlei18n, "en-US")) === selectedPlanId,
	);
	if (!item?.titlei18n) {
		return null;
	}
	return {
		rowLabel: findStepLabel(steps, "plan", locale),
		value: resolveI18n(item.titlei18n, locale),
	};
};

const buildSkillsRow = (
	steps: OnboardingStep[],
	selectedSkillIds: string[],
	locale: string,
): ReviewRowData | null => {
	const step = steps.find((s) => s.step === "skill");
	if (selectedSkillIds.length === 0) {
		return null;
	}
	if (!step?.data) {
		return null;
	}
	const skillTitles = selectedSkillIds.map((id) => {
		const item = step.data?.find(
			(d) => (d.type ?? resolveI18n(d.titlei18n, "en-US")) === id,
		);
		return resolveI18n(item?.titlei18n, locale) || id;
	});
	return {
		rowLabel: findStepLabel(steps, "skill", locale),
		value: formatList(locale, skillTitles),
	};
};

const ReviewRow = ({ rowLabel, value }: ReviewRowData) => {
	const theme = useAppTheme();

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			paddingVertical={12}
			borderBottomWidth={1}
			borderColor={theme.colors.surfaceVariant}
			gap={8}
		>
			<Text
				variant="labelSmall"
				style={{
					color: theme.colors.onSurfaceVariant,
					fontWeight: 600,
					flex: 1,
				}}
			>
				{rowLabel}
			</Text>
			<Text
				variant="titleMedium"
				style={{
					color: theme.colors.onBackground,
					flex: 2,
					fontWeight: 400,
					textAlign: "right",
				}}
			>
				{value}
			</Text>
		</Box>
	);
};

const CompletionChoiceCard = ({
	selected,
	icon,
	title,
	body,
	badge,
	onPress,
}: {
	selected: boolean;
	icon: IconType;
	title: string;
	body: string;
	badge?: string;
	onPress: () => void;
}) => {
	const theme = useAppTheme();

	return (
		<List.Item
			accessibilityRole="radio"
			accessibilityState={{ checked: selected }}
			aria-checked={selected}
			onPress={onPress}
			style={{
				borderWidth: 1,
				borderColor: selected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				borderRadius: theme.roundness * 2,
				backgroundColor: selected
					? theme.colors.elevation.level1
					: theme.colors.surface,
				paddingHorizontal: 16,
				paddingVertical: 12,
			}}
			contentStyle={{ gap: 4, paddingRight: 8 }}
			left={() => (
				<Box
					style={{
						width: 36,
						height: 36,
						borderRadius: theme.roundness * 99,
						backgroundColor: selected
							? theme.colors.elevation.level5
							: theme.colors.background,
						justifyContent: "center",
						alignItems: "center",
						alignSelf: "center",
					}}
				>
					<Icon
						name={icon}
						size={24}
						color={
							selected ? theme.colors.secondary : theme.colors.inverseSurface
						}
					/>
				</Box>
			)}
			title={title}
			titleStyle={{ fontWeight: 600, color: theme.colors.onSurface }}
			titleNumberOfLines={2}
			description={body}
			descriptionStyle={{ color: theme.colors.onSurfaceVariant }}
			right={
				badge
					? () => (
							<Box
								style={{
									paddingHorizontal: 8,
									paddingVertical: 4,
									borderRadius: theme.roundness * 4,
									backgroundColor: theme.colors.surfaceVariant,
									flexDirection: "row",
									alignItems: "center",
									alignSelf: "center",
									gap: 4,
								}}
							>
								<Icon
									name="clock-outline"
									size={14}
									color={theme.colors.onSurfaceVariant}
								/>
								<Text
									variant="labelSmall"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{badge}
								</Text>
							</Box>
						)
					: undefined
			}
		/>
	);
};

export const OnboardingReviewScreen = ({ title, description }: Props) => {
	const theme = useAppTheme();
	const { t, i18n } = useTranslation();
	const locale = i18n.language;
	const {
		steps,
		exams,
		categories,
		diagnostics,
		derivedStates,
		filteredExams,
		selectedLearnerTypeId,
		selectedRankId,
		selectedCategoryId,
		selectedLevelId,
		selectedStateCode,
		selectedExamId,
		selectedExamDate,
		selectedSkillIds,
		selectedPlanId,
		selectedProfile,
		selectedDiagnostic,
		onSelectDiagnostic,
	} = useOnboarding();

	const diagnosticOptions = useMemo(
		() => getAvailableDiagnostics(diagnostics, selectedSkillIds, locale),
		[diagnostics, selectedSkillIds, locale],
	);
	const [recommendedDiagnosticOption] = diagnosticOptions;
	const skillItems = steps.find((step) => step.step === "skill")?.data;

	const didDefaultDiagnostic = useRef(false);
	useEffect(() => {
		if (didDefaultDiagnostic.current || !recommendedDiagnosticOption) {
			return;
		}

		didDefaultDiagnostic.current = true;
		onSelectDiagnostic(recommendedDiagnosticOption.diagnostic);
	}, [recommendedDiagnosticOption, onSelectDiagnostic]);

	const displayName = [selectedProfile?.firstName, selectedProfile?.lastName]
		.filter(Boolean)
		.join(" ");

	const rows = [
		buildLearnerTypeRow(steps, selectedLearnerTypeId, locale),
		buildCategoryRow(steps, categories, selectedCategoryId, locale),
		buildLevelRow(steps, selectedLevelId, locale),
		buildStateRow(steps, derivedStates, selectedStateCode, locale),
		buildExamRow(steps, exams, filteredExams, selectedExamId, locale),
		buildExamDateRow(steps, selectedExamDate, locale),
		buildRankRow(steps, selectedRankId, locale),
		buildSkillsRow(steps, selectedSkillIds, locale),
		buildPlanRow(steps, selectedPlanId, locale),
	].filter((r): r is ReviewRowData => r !== null);

	return (
		<OnboardingBox>
			<Box gap={14}>
				<Text variant="titleLarge">{title}</Text>
			</Box>
			{selectedProfile?.username ? (
				<Box
					paddingHorizontal={20}
					paddingVertical={14}
					backgroundColor={theme.colors.elevation.level1}
					borderRadius={theme.roundness * 2}
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					gap={16}
				>
					<Box flex={1} flexDirection="row" alignItems="center" gap={12}>
						<Avatar size={60} username={selectedProfile.username} />
						<Box flex={1}>
							<Text variant="titleMedium">{selectedProfile.username}</Text>
							{displayName ? (
								<Text
									variant="bodyMedium"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{displayName}
								</Text>
							) : null}
						</Box>
					</Box>
					<AppIcon
						name="checkbox-marked-circle-outline"
						size={24}
						color={theme.colors.primary}
					/>
				</Box>
			) : null}
			{rows.length > 0 ? (
				<Box>
					{rows.map((row) => (
						<ReviewRow
							key={row.rowLabel}
							rowLabel={row.rowLabel}
							value={row.value}
						/>
					))}
				</Box>
			) : null}
			{diagnosticOptions.length > 0 ? (
				<>
					<Box gap={12} accessibilityRole="radiogroup">
						{description ? (
							<Text
								variant="bodyMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{description}
							</Text>
						) : null}
						{diagnosticOptions.map(
							({ diagnostic, title: optionTitle, hasTopics, topic }) => (
								<CompletionChoiceCard
									key={diagnostic.id}
									selected={selectedDiagnostic?.id === diagnostic.id}
									icon={getDiagnosticIcon(topic, skillItems) as IconType}
									title={
										hasTopics
											? t("onboarding.steps.review.diagnosticChoice.title", {
													title: optionTitle,
												})
											: optionTitle
									}
									body={t("onboarding.steps.review.diagnosticChoice.body", {
										count: diagnostic.numItems,
									})}
									badge={t(
										"onboarding.steps.review.diagnosticChoice.duration",
										{
											minutes: Math.max(
												1,
												Math.round(diagnostic.totalTime / 60),
											),
										},
									)}
									onPress={() => onSelectDiagnostic(diagnostic)}
								/>
							),
						)}
						<CompletionChoiceCard
							selected={!selectedDiagnostic}
							icon="arrow-right-bold-circle-outline"
							title={t("onboarding.steps.review.homeChoice.title")}
							body={t("onboarding.steps.review.homeChoice.body")}
							onPress={() => onSelectDiagnostic(undefined)}
						/>
					</Box>
				</>
			) : null}
		</OnboardingBox>
	);
};
